import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { LOADER_SIZE, LOADER_LABEL_VARIANTS, LOADER_VARIANTS } from '../constants';
import { ProgressTrack, LoaderLabel, ProgressIndicator } from '../elements';

const StyledDeterminateContainer = styled.div`
  max-width: 784px;
`;

const StyledProgressBarWrapper = styled.div`
  cursor: progress;
  display: flex;
  flex-direction: row;
  max-width: ${({ trackWidth }) => `calc(${trackWidth} + 16px)`};
`;

const StyledProgressLabelWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: ${({ hideLabel }) => (hideLabel ? 'flex-end' : 'space-between')};
  max-width: ${({ trackWidth }) => `calc(${trackWidth} + 16px)`};
`;

const DeterminateProgressBar = ({
  className,
  dataTestId = '',
  hideLabel = false,
  hideProgress = false,
  isInline = false,
  label = 'Loading...',
  loaderSize = LOADER_SIZE.LARGE,
  percentComplete,
  trackWidth,
  ...other
}) => {
  return (
    <StyledDeterminateContainer
      aria-label="Determinate Progress Bar"
      aria-valuenow={Math.round(percentComplete)}
      aria-valuemin="0"
      aria-valuemax="100"
      data-testid={dataTestId ? `${dataTestId}-progress-bar` : undefined}
      role="progressbar"
    >
      {!isInline && (
        <>
          <StyledProgressLabelWrapper
            data-testid={dataTestId ? `${dataTestId}-progress-label-wrapper` : undefined}
            role="status"
            {...{ hideLabel, trackWidth }}
          >
            <LoaderLabel
              labelMaxWidth={trackWidth}
              labelVariant={LOADER_LABEL_VARIANTS.DETERMINATE}
              {...{ dataTestId, hideLabel, hideProgress, isInline, label, loaderSize }}
            />
            <ProgressIndicator
              labelMaxWidth={trackWidth}
              labelVariant={LOADER_LABEL_VARIANTS.DETERMINATE}
              progressLabel={percentComplete}
              {...{ dataTestId, hideLabel, hideProgress, isInline, loaderSize }}
            />
          </StyledProgressLabelWrapper>
          <StyledProgressBarWrapper
            data-testid={dataTestId ? `${dataTestId}-progress-bar-wrapper` : undefined}
            trackWidth={trackWidth}
            {...other}
          >
            <ProgressTrack
              variant={LOADER_VARIANTS.DETERMINATE}
              {...{ className, dataTestId, loaderSize, percentComplete, trackWidth }}
            />
          </StyledProgressBarWrapper>
        </>
      )}
      {isInline && (
        <StyledProgressBarWrapper
          data-testid={dataTestId ? `${dataTestId}-progress-bar-wrapper` : undefined}
          isInline={isInline}
          role="status"
          {...other}
        >
          <StyledProgressLabelWrapper data-testid={dataTestId ? `${dataTestId}-progress-label-wrapper` : undefined}>
            <LoaderLabel
              labelMaxWidth={trackWidth}
              labelVariant={LOADER_LABEL_VARIANTS.DETERMINATE}
              {...{ dataTestId, hideLabel, isInline, label, loaderSize }}
            >
              {label}
            </LoaderLabel>
          </StyledProgressLabelWrapper>
          <ProgressTrack
            variant={LOADER_VARIANTS.DETERMINATE}
            {...{ className, dataTestId, loaderSize, percentComplete, trackWidth }}
          />
          <ProgressIndicator
            progressLabel={percentComplete}
            labelMaxWidth={trackWidth}
            labelVariant={LOADER_LABEL_VARIANTS.DETERMINATE}
            {...{ dataTestId, hideProgress, isInline, loaderSize }}
          />
        </StyledProgressBarWrapper>
      )}
    </StyledDeterminateContainer>
  );
};

DeterminateProgressBar.propTypes = {
  /** Adds additional class name to progress bar track */
  className: PropTypes.string,
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** If true, label will be inline with progress loader and will have a max-width of 194px. */
  isInline: PropTypes.bool,
  /** Visually hides loader text */
  hideLabel: PropTypes.bool,
  /** Visually hides percent progress text */
  hideProgress: PropTypes.bool,
  /** Adds message to left of loader */
  label: PropTypes.node,
  /** Sets the size of the loader to small or large */
  loaderSize: PropTypes.oneOf(Object.values(LOADER_SIZE)),
  /** Determines the width of the progress track fill based on percent complete */
  percentComplete: PropTypes.number.isRequired,
  /** Determines the width property of the progress track with a max-width of 768px */
  trackWidth: PropTypes.string.isRequired,
};

export { DeterminateProgressBar };
